import { useEffect, useMemo } from 'react';
import useLocalStorageState from '../../../utils/useLocalStorageState';
import useGetDivisions from '../useGetDivisions';
import {
    FILTER_TYPES,
    formatFiltersForApi,
    isAppliedFilterValue,
    renderAppliedFiltersDescription,
} from '../../../utils/filterUtils';
import {
    NEEDS_REVIEW_REASONS,
    PROCESSED_TIMESHEET_STATUSES,
    EXCLUDE_SPECIAL_PROCESSING,
    specialProcessingFilterOptions,
} from '../../constants';
import StatusPill from '../../../Shared/Components/StatusPill';

export const reviewReasonsOptions = Object.values(NEEDS_REVIEW_REASONS).map(({ label, name }) => ({
    label,
    value: name,
}));

export { EXCLUDE_SPECIAL_PROCESSING, specialProcessingFilterOptions };

/**
 * Special Processing mixes CHECKBOX include values with one boolean exclude option at
 * the top of the list. This is not FILTER_TYPES.FLAG (used by Corrections Only /
 * Exclude Corrections), where every selected item serializes as a boolean query param.
 *
 * Conflict handling is defined on specialProcessingFilterOptions. This formatter
 * sends ExcludeSpecialProcessing=true and omits SpecialProcessing include values
 * (the API returns 400 if both are sent).
 */
export const formatProcessedTimesheetFiltersForApi = (filters, filtersSetup) => {
    const selected = filters?.specialProcessing ?? [];
    const excludeSpecialProcessing = selected.includes(EXCLUDE_SPECIAL_PROCESSING);
    const includeValues = excludeSpecialProcessing
        ? []
        : selected.filter((value) => value !== EXCLUDE_SPECIAL_PROCESSING);

    const formatted = formatFiltersForApi(
        { ...filters, specialProcessing: includeValues },
        filtersSetup
    );

    return {
        ...formatted,
        ...(excludeSpecialProcessing ? { [EXCLUDE_SPECIAL_PROCESSING]: true } : {}),
    };
};

const getDefaultStatuses = (storageKey) => {
    if (storageKey === 'processedTimesheetsBP') return ['BPReview', 'BPQuestion'];
    if (storageKey === 'processedTimesheetsTC') return ['TCReview', 'TCHold'];
    return [];
};

const filterStatusList = { ...PROCESSED_TIMESHEET_STATUSES };
delete filterStatusList.Creating;
delete filterStatusList.NeedsReview;

const useProcessedTimeseetsFilters = (storageKey = '') => {
    const { data: divisions = [], isLoading: isLoadingDivisions } = useGetDivisions();
    const { state: filters, setValue: setFilters } = useLocalStorageState(`${storageKey}Filters`);
    const handleApplyFilter = (newFilters) => setFilters(newFilters);

    const filtersSetup = useMemo(
        () => [
            {
                name: 'statuses',
                label: 'Status',
                type: FILTER_TYPES.CHECKBOX,
                defaultValue: getDefaultStatuses(storageKey),
                overrides: {
                    options: Object.keys(filterStatusList),
                    render: (value) => <StatusPill status={value} />,
                },
                isQuickFilter: storageKey === 'processedTimesheetsBP',
            },
            {
                name: 'reviewReasons',
                label: 'Review Reasons',
                type: FILTER_TYPES.CHECKBOX,
                defaultValue: [],
                overrides: { options: reviewReasonsOptions },
                isQuickFilter: storageKey === 'processedTimesheetsTC',
            },
            {
                name: 'divisions',
                label: 'Divisions',
                type: FILTER_TYPES.CHECKBOX,
                defaultValue: [],
                overrides: {
                    options: divisions.map((d) => ({ label: d.description, value: d.name })),
                    emptyLabel: 'All',
                },
            },
            {
                name: 'specialProcessing',
                label: 'Special Processing',
                type: FILTER_TYPES.CHECKBOX,
                defaultValue: [],
                overrides: {
                    options: specialProcessingFilterOptions,
                    emptyLabel: 'All',
                },
            },
            {
                name: 'payrollDate',
                label: 'Payroll Date',
                type: FILTER_TYPES.DATE_RANGE,
                defaultValue: { from: null, to: null },
            },
            {
                name: 'weekEndingDate',
                label: 'Week Ending Date',
                type: FILTER_TYPES.DATE_RANGE,
                defaultValue: { from: null, to: null },
            },
            {
                name: 'monthPosted',
                label: 'Month Posted',
                type: FILTER_TYPES.DATE_RANGE,
                defaultValue: { from: null, to: null },
            },
            {
                name: 'flags',
                label: 'Flags',
                type: FILTER_TYPES.FLAG,
                defaultValue: [],
                overrides: {
                    options: [
                        {
                            label: 'Corrections Only',
                            value: 'CorrectionsOnly',
                            exclude: ['ExcludeCorrections'],
                        },
                        {
                            label: 'Exclude Corrections',
                            value: 'ExcludeCorrections',
                            exclude: ['CorrectionsOnly'],
                        },
                    ],
                    emptyLabel: 'All',
                },
            },
        ],
        [divisions]
    );

    useEffect(() => {
        if (filters) return;
        // If filters are not set, set them to the default values
        const defaultFiltersValue = {};
        filtersSetup.forEach(({ name, defaultValue }) => {
            if (isAppliedFilterValue(defaultValue)) {
                defaultFiltersValue[name] = defaultValue;
            }
        });
        if (Object.keys(defaultFiltersValue).length > 0) {
            setFilters(defaultFiltersValue);
        }
    }, [filtersSetup]);

    const appliedFiltersDescription = useMemo(
        () => renderAppliedFiltersDescription(filters, filtersSetup, { initialSeparator: '' }),
        [filters, filtersSetup]
    );

    if (!storageKey) {
        throw new Error(
            'Storage key is required and should be unique to prevent overwriting data.'
        );
    }

    return {
        filters,
        setFilters,
        filtersSetup,
        handleApplyFilter,
        isLoadingFilterConfig: isLoadingDivisions,
        appliedFiltersDescription,
    };
};

export default useProcessedTimeseetsFilters;
